import React, { useCallback, useEffect, useRef, useState } from 'react';

import {
    selectEmojiesToSend,
    applyEmojies,
    selectEmojiesEnabled,
    selectUserEmoji,
    selectIsVideoOnline,
    toggleOnline,
    pingEmoji,
} from 'ducks/emojies';
import { useDispatch, useSelector } from 'react-redux';
import { selectCurrentUser } from 'ducks/user';
import { cnKhEmojies } from './KhEmojies.cn';
import './KhEmojies.css';
import { emojies as emojiesMap, specialEmojiIndex, isSpecialEmojiActive } from './KhEmojies';

import Goose from './goose.png';
import SpecialEmoji from './specialEmoji.png';

const KhEmojiesScreenComponent = () => {
    const overlayRef = useRef<HTMLDivElement>(null);
    const emojies = useSelector(selectEmojiesToSend);
    const userEmoji = useSelector(selectUserEmoji);
    const dispatch = useDispatch();

    const handleRemoveFlyingEmoji = useCallback(node => {
        if (!overlayRef.current) return;
        overlayRef.current.removeChild(node);
    }, []);

    const handleDisplayFlyingEmoji = useCallback(
        (emojiIndex: string, isUserEmoji = false) => {
            if (!overlayRef.current) {
                return;
            }

            const isSpecialEmoji = isSpecialEmojiActive ?
                (Number(emojiIndex) === specialEmojiIndex ||
                Number(emojiIndex) === specialEmojiIndex + 14) :
                false;

            const emoji = isSpecialEmoji ? 'specialEmoji' : emojiesMap[Number(emojiIndex)];

            if (!emoji) {
                return null;
            }

            const isLargeEmoji = !isSpecialEmoji && Number(emojiIndex) > 13 ||
                Number(emojiIndex) === specialEmojiIndex + 14;
            const isGoose = emoji === 'goose';
            /** Индексы эмодзи пальцев вверх/вниз и ракеты. Им запрещаем вращаться */
            const shouldMove = ![4, 9, 13, 17, 22, 26,
                specialEmojiIndex, specialEmojiIndex + 14].includes(Number(emojiIndex));

            const node = document.createElement(isGoose || isSpecialEmoji ? 'img' : 'div');
            if (isGoose || isSpecialEmoji) {
                // @ts-ignore
                node.src = isSpecialEmoji ? SpecialEmoji : Goose;
            } else {
                node.appendChild(document.createTextNode(emoji));
            }
            node.className = `emoji wiggle-${Math.floor(Math.random() * 10) + 1}`;

            node.style.left = `${Math.random() * 100}%`;
            if (!isUserEmoji) {
                node.style.animationDelay = `${Math.random() * 1600}ms`;
            } else {
                node.style.filter = 'drop-shadow(0 0 2px #00fd379f) drop-shadow(0 0 1px #00ff15ff)';
            }

            if (isLargeEmoji) {
                node.style.fontSize = '60px';

                if (isGoose || isSpecialEmoji) {
                    node.style.minWidth = '60px';
                    node.style.minHeight = '60px';
                }
            }
            if (shouldMove) {
                node.style.transform = `rotate(${-30 + 10 * Math.random() * Math.random() * 60}deg)`;
            }
            overlayRef.current.appendChild(node);

            node.addEventListener('animationend', e => handleRemoveFlyingEmoji(e.target));
        },
        [handleRemoveFlyingEmoji],
    );

    useEffect(() => {
        if (emojies.length) {
            emojies.forEach(e => handleDisplayFlyingEmoji(e));
        }
    }, [emojies, dispatch, handleDisplayFlyingEmoji]);

    useEffect(
        () => () =>
            overlayRef.current?.childNodes.forEach(n =>
                n.removeEventListener('animationend', handleRemoveFlyingEmoji),
            ),
        [handleRemoveFlyingEmoji],
    );

    useEffect(() => {
        if (userEmoji || userEmoji === 0) {
            handleDisplayFlyingEmoji(String(userEmoji), true);
        }
    }, [userEmoji, handleDisplayFlyingEmoji]);

    return (
        <div
            style={{
                width: '100%',
                height: '40%',
                position: 'absolute',
                overflow: 'hidden',
                opacity: '0.8',
                bottom: '0',
                zIndex: 1,
                pointerEvents: 'none',
            }}
    >
            <div className={cnKhEmojies('screen')} ref={overlayRef} />
        </div>
    );
};

export const KhEmojiesScreen = () => {
    const [socket, setSocket] = useState(null);
    const isEmojiesEnabled = useSelector(selectEmojiesEnabled);
    const isOnline = useSelector(selectIsVideoOnline);
    const user = useSelector(selectCurrentUser);
    const dispatch = useDispatch();
    const initialTimeDeltaRef = useRef(0);
    const lastTimeUpdateHandledAt = useRef(0);
    const visibilityRef = useRef(true);

    const handleMessage = useCallback(
        event => {
            if (isOnline && visibilityRef.current) {
                const { message } = event.detail;

                try {
                    dispatch(applyEmojies(JSON.parse(message)));
                } catch {}
            }
        },
        [dispatch, isOnline],
    );

    useEffect(() => {
        function onVisibilityChange() {
            visibilityRef.current = document.visibilityState === 'visible';
        }

        document.addEventListener('visibilitychange', onVisibilityChange);

        return () => {
            document.removeEventListener('visibilitychange', onVisibilityChange);
        };
    }, []);

    useEffect(() => {
        if (!socket && isEmojiesEnabled === true && isOnline) {
            const socket = new window.Ya.XIVA({
                api: 'wss://push.yandex-team.ru/v2/subscribe/websocket',
                urlParams: {
                    service: 'khural-reactions',
                    user: `${user.uid}`,
                    client: 'client',
                    session: user.id,
                },
            });

            setSocket(socket);
        }

        if (!isEmojiesEnabled || !isOnline) {
            socket?.destruct();
            setSocket(null);
        }

        return () => {
            if (socket) {
                socket?.destruct();
            }
        };
    }, [socket, user.id, user.uid, isEmojiesEnabled, isOnline]);

    useEffect(() => {
        const listener = (event: MessageEvent) => {
            try {
                if (event.origin === 'https://frontend.vh.yandex-team.ru' || event.origin === 'https://runtime.strm.yandex-team.ru') {
                    const data = JSON.parse(event.data);

                    if (initialTimeDeltaRef.current === 0 && data.event === 'timeupdate' && data.time > 0) {
                        initialTimeDeltaRef.current = Date.now() / 1000 - data.time;
                        dispatch(toggleOnline(true));
                    } else if (data.event === 'rewound') {
                        const newDelta = Date.now() / 1000 - data.time;
                        const isBehind = Math.abs(newDelta - initialTimeDeltaRef.current) > 10;

                        dispatch(toggleOnline(!isBehind));
                    } else if (data.event === 'paused') {
                        dispatch(toggleOnline(false));
                    } else if (data.event === 'resumed') {
                        const newDelta = Date.now() / 1000 - data.time;
                        const isBehind = Math.abs(newDelta - initialTimeDeltaRef.current) > 10;

                        if (!isBehind) {
                            dispatch(toggleOnline(true));
                        }
                    } else if (data.event === 'timeupdate' && initialTimeDeltaRef.current !== 0) {
                        const now = Date.now();
                        /** Реагируем на timeupdate раз в 10 секунд */
                        if (now - lastTimeUpdateHandledAt.current > 10_000) {
                            lastTimeUpdateHandledAt.current = now;
                            const newDelta = Date.now() / 1000 - data.time;
                            const isBehind = Math.abs(newDelta - initialTimeDeltaRef.current) > 10;

                            dispatch(toggleOnline(!isBehind));
                        }
                    }
                }
            } catch {}
        };

        window.addEventListener('message', listener);

        return () => {
            window.removeEventListener('message', listener);
        };
    }, [isOnline, dispatch]);

    useEffect(() => {
        if (socket) {
            socket.addEventListener('message', handleMessage);
        }

        return () => {
            if (socket) {
                socket.removeEventListener('message', handleMessage);
            }
        };
    }, [handleMessage, socket]);

    useEffect(() => {
        dispatch(pingEmoji());
    }, [dispatch]);

    if (!isEmojiesEnabled || isEmojiesEnabled === 'unavailable') {
        return null;
    }

    return <KhEmojiesScreenComponent />;
};
